import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import {
  Loader2, UploadCloud, Image as ImageIcon, Film, FileText, Music2,
  Copy, Check, Trash2,
} from "lucide-react";

const CHUNK = 5 * 1024 * 1024; // 5MB
const ACCEPT = "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.rtf,.ppt,.pptx,.xls,.xlsx";

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const KindIcon = ({ kind, className }) => {
  if (kind === "video") return <Film className={className} />;
  if (kind === "audio") return <Music2 className={className} />;
  if (kind === "image") return <ImageIcon className={className} />;
  return <FileText className={className} />;
};

export default function AdminMedia() {
  const [token] = useState(() => getAdminToken());
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [queue, setQueue] = useState([]); // {name, pct, status}
  const [copied, setCopied] = useState("");
  const inputRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/media`, auth);
      setAssets(data.assets || []);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load your media library.");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const uploadOne = async (file, onPct) => {
    const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, "").slice(0, 32).padEnd(16, "0");
    const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < chunks; i++) {
      const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("file", blob, file.name);
      await axios.post(`${API}/admin/media/upload-chunk`, fd, {
        headers: { ...auth.headers },
        onUploadProgress: (ev) => {
          const chunkFrac = ev.total ? ev.loaded / ev.total : 1;
          onPct(Math.round(((i + chunkFrac) / chunks) * 95));
        },
      });
    }
    onPct(97);
    const { data } = await axios.post(`${API}/admin/media/upload-complete`, { upload_id: uploadId, filename: file.name }, auth);
    onPct(100);
    return data;
  };

  const handleFiles = async (files) => {
    setErr("");
    const list = Array.from(files);
    for (const file of list) {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 6)}`;
      setQueue((q) => [...q, { key, name: file.name, pct: 0, status: "uploading" }]);
      try {
        const asset = await uploadOne(file, (pct) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, pct } : x))));
        setAssets((a) => [asset, ...a]);
        setQueue((q) => q.map((x) => (x.key === key ? { ...x, status: "done", pct: 100 } : x)));
        setTimeout(() => setQueue((q) => q.filter((x) => x.key !== key)), 1500);
      } catch (e) {
        if (e.response?.status === 401) { adminLogout(); return; }
        setQueue((q) => q.map((x) => (x.key === key ? { ...x, status: "error" } : x)));
        setErr(e.response?.data?.detail || `Could not upload ${file.name}.`);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const copyUrl = async (asset) => {
    const abs = `${API}/media/${asset.id}`;
    try { await navigator.clipboard.writeText(abs); setCopied(asset.id); setTimeout(() => setCopied((k) => (k === asset.id ? "" : k)), 2000); } catch (e) { /* ignore */ }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Remove "${asset.title}" from your library?`)) return;
    try {
      await axios.delete(`${API}/admin/media/${asset.id}`, auth);
      setAssets((a) => a.filter((x) => x.id !== asset.id));
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not remove that file.");
    }
  };

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-media">
      <div className="mx-auto max-w-5xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <h1 className="font-display text-3xl text-ink">Media Library</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-soft">
          Upload images, video, audio and documents to use when creating content for your readers.
          Copy any file's link and paste it into a Vault entry, email, or the website editor.
        </p>

        {/* Uploader */}
        <label
          data-testid="media-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files); }}
          className="mt-6 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-ink/20 bg-ivory-50 px-6 py-10 text-center transition hover:border-gold/50"
        >
          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" data-testid="media-file-input"
            onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} />
          <UploadCloud className="h-8 w-8 text-ink-soft" />
          <p className="mt-3 font-display text-ink">Drop files here, or click to choose</p>
          <p className="mt-1 text-xs text-ink-muted">Images, video, audio & documents · up to 500MB each</p>
        </label>

        {err && <p className="mt-3 text-sm text-terracotta" data-testid="media-error">{err}</p>}

        {/* Upload queue */}
        {queue.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="media-queue">
            {queue.map((q) => (
              <div key={q.key} className="rounded-lg bg-ivory-50 p-3 ring-1 ring-ivory-400/60">
                <div className="flex items-center justify-between text-sm text-ink">
                  <span className="truncate">{q.name}</span>
                  <span className="ml-3 flex-none text-xs text-ink-muted">
                    {q.status === "error" ? "Failed" : q.status === "done" ? "Done" : `${q.pct}%`}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
                  <div className={`h-full rounded-full transition-all ${q.status === "error" ? "bg-terracotta" : "bg-gold"}`} style={{ width: `${q.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Library grid */}
        <div className="mt-8 pb-16">
          {loading ? (
            <div className="flex items-center gap-2 text-ink-soft"><Loader2 className="h-4 w-4 animate-spin" /> Loading your library…</div>
          ) : assets.length === 0 ? (
            <p className="text-sm text-ink-muted" data-testid="media-empty">No files yet. Upload your first image, video or document above.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
              {assets.map((a) => (
                <div key={a.id} data-testid={`media-card-${a.id}`} className="group flex flex-col overflow-hidden rounded-xl bg-ivory-50 ring-1 ring-ivory-400/60">
                  <div className="relative aspect-square w-full overflow-hidden bg-ink/5">
                    {a.kind === "image" ? (
                      <img src={`${API}/media/${a.id}`} alt={a.title} loading="lazy" className="h-full w-full object-cover" />
                    ) : a.kind === "video" ? (
                      <video src={`${API}/media/${a.id}`} className="h-full w-full object-cover" muted preload="metadata" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <KindIcon kind={a.kind} className="h-10 w-10 text-ink-soft/60" />
                      </div>
                    )}
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-ink/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-ivory-50">
                      <KindIcon kind={a.kind} className="h-3 w-3" /> {a.kind}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-3">
                    <p className="truncate text-sm text-ink" title={a.title}>{a.title}</p>
                    <p className="text-xs text-ink-muted">{fmtSize(a.size)}</p>
                    <div className="mt-2.5 flex items-center gap-2">
                      <button onClick={() => copyUrl(a)} data-testid={`media-copy-${a.id}`}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-xs text-ivory-50 hover:bg-ink/90">
                        {copied === a.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === a.id ? "Copied" : "Copy link"}
                      </button>
                      <button onClick={() => remove(a)} data-testid={`media-delete-${a.id}`}
                        className="inline-flex items-center justify-center rounded-full border border-terracotta/40 p-1.5 text-terracotta hover:bg-terracotta/10">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
